/**
 * BackgroundJobsContext
 *
 * Manages background job execution and notifications across the application.
 * Allows users to start jobs and navigate away while jobs run in the background.
 * Provides notifications when jobs complete.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  getDoc,
  doc,
  QuerySnapshot,
  DocumentData,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { ReportStatus } from '../features/sector-research/types/sectorResearch.types';
import { showJobCompleteToast, showJobErrorToast } from '../lib/toast';
import { extractReportMetrics } from '../hooks/useJobNotifications';

// ============================================================================
// TYPES
// ============================================================================

const OVERVIEW_PREVIEW_LENGTH = 140;
const JOB_DISCOVERY_CONCURRENCY = 4, MAX_LIVE_JOB_PROJECTS = 12, JOB_POLL_INTERVAL_MS = 30_000, IDLE_JOB_POLL_INTERVAL_MS = 300_000;

const formatOverviewPreview = (overview?: string): string | undefined => {
  if (!overview) return undefined;
  const trimmed = overview.trim();
  if (!trimmed) return undefined;
  return trimmed.length > OVERVIEW_PREVIEW_LENGTH
    ? `${trimmed.slice(0, OVERVIEW_PREVIEW_LENGTH - 1)}…`
    : trimmed;
};

export interface BackgroundJob {
  id: string;
  projectId: string;
  sectorId?: string;
  type: 'sector-research' | 'financial-extraction' | 'document-processing';
  status: ReportStatus | 'queued' | 'processing';
  progressPercent: number;
  progressMessage: string;
  startedAt: Timestamp;
  completedAt?: Timestamp;
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
  researchOverview?: string;
}

export interface JobNotification {
  id: string;
  jobId: string;
  projectId: string;
  type: 'completion' | 'error' | 'warning';
  title: string;
  message: string;
  timestamp: Timestamp;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  researchOverview?: string;
}

interface BackgroundJobsContextType {
  // Active jobs
  activeJobs: BackgroundJob[];

  // Notifications
  notifications: JobNotification[];
  unreadCount: number;

  // Operations
  startJob: (
    projectId: string,
    type: BackgroundJob['type'],
    jobId: string
  ) => void;
  dismissJob: (jobId: string) => void;
  markNotificationRead: (notificationId: string) => void;
  markAllRead: () => void;
  clearNotifications: () => void;

  // Status checking
  getJobStatus: (jobId: string) => BackgroundJob | null;
  hasActiveJobs: boolean;
}

const BackgroundJobsContext = createContext<BackgroundJobsContextType | undefined>(
  undefined
);

// ============================================================================
// PROVIDER
// ============================================================================

interface BackgroundJobsProviderProps {
  children: ReactNode;
}

export function BackgroundJobsProvider({ children }: BackgroundJobsProviderProps) {
  const { user } = useAuth();
  const userId = user?.uid;
  const [activeJobs, setActiveJobs] = useState<BackgroundJob[]>([]);
  const [notifications, setNotifications] = useState<JobNotification[]>([]);
  const notifiedJobIdsRef = useRef(new Set<string>());
  const refreshProjectRef = useRef<(projectId: string) => void>(() => undefined);

  useEffect(() => {
    setActiveJobs([]);
    setNotifications([]);
    notifiedJobIdsRef.current.clear();
    if (!userId) return;

    // Watching two queries for every historical deal overwhelms Firestore's listen
    // stream on large accounts. Discover jobs with bounded reads, watch active
    // projects only, and poll the remainder without dropping their progress.
    let disposed = false, draining = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let nextIdleSweepAt = Date.now() + IDLE_JOB_POLL_INTERVAL_MS;
    let projectIds = new Set<string>();
    const pendingProjects = new Set<string>();
    const jobsByProject = new Map<string, BackgroundJob[]>();
    const unsubscribeJobs = new Map<string, () => void>();
    const activeQuery = (projectId: string) => query(
      collection(db, 'projects', projectId, 'sectorResearchJobs'),
      where('status', 'in', ['initializing', 'queued', 'identifying', 'researching', 'analyzing', 'synthesizing', 'processing'])
    );
    const publishJobs = () => {
      if (!disposed) setActiveJobs([...jobsByProject.values()].flat());
    };
    const notifyCompletion = (projectId: string, jobId: string, job: DocumentData) => {
      const notificationKey = `${projectId}:${jobId}`;
      if (disposed || !projectIds.has(projectId) || notifiedJobIdsRef.current.has(notificationKey)
        || !['completed', 'error', 'cancelled'].includes(job.status)) return;
      notifiedJobIdsRef.current.add(notificationKey);
      const overviewSnippet = formatOverviewPreview(job.researchOverview || job.metadata?.researchOverview);
      const notification: JobNotification = {
        id: `${jobId}-${Date.now()}`, jobId, projectId,
        type: job.status === 'completed' ? 'completion' : job.status === 'error' ? 'error' : 'warning',
        title: job.status === 'completed' ? 'Sector Research Complete' : job.status === 'error' ? 'Sector Research Failed' : 'Sector Research Cancelled',
        message: job.status === 'completed'
          ? overviewSnippet ? `Report ready for "${overviewSnippet}"` : 'Your sector research report is ready to view'
          : job.status === 'error'
            ? overviewSnippet ? `Research failed for "${overviewSnippet}"` : job.error?.message || 'An error occurred'
            : overviewSnippet ? `Research cancelled for "${overviewSnippet}"` : 'Report generation was cancelled',
        timestamp: Timestamp.now(), read: false,
        actionUrl: `/project/${projectId}?tab=sector-research`,
        actionLabel: job.status === 'completed' ? 'View Report' : undefined,
        researchOverview: overviewSnippet,
      };
      setNotifications((previous) => [notification, ...previous]);
      if (job.status === 'completed') {
        showJobCompleteToast(jobId, job.researchOverview || 'Sector Research', extractReportMetrics(job), undefined, undefined);
      } else if (job.status === 'error') {
        showJobErrorToast(jobId, job.researchOverview || 'Sector Research', {
          code: job.error?.code || 'unknown-error', message: job.error?.message || 'An error occurred', retryable: job.error?.retryable ?? true,
        });
      }
    };
    const handleSnapshot = async (projectId: string, snapshot: QuerySnapshot<DocumentData>) => {
      if (disposed || !projectIds.has(projectId)) return;
      const previousJobs = jobsByProject.get(projectId) || [];
      const jobs: BackgroundJob[] = snapshot.docs.map((document) => {
        const data = document.data();
        return {
          id: document.id, projectId, sectorId: data.sectorId, type: 'sector-research', status: data.status,
          progressPercent: data.progressPercent || 0, progressMessage: data.progressMessage || '',
          startedAt: data.startedAt, completedAt: data.completedAt, error: data.error,
          researchOverview: data.researchOverview || data.metadata?.researchOverview,
        };
      });
      jobsByProject.set(projectId, jobs);
      if (previousJobs.length || jobs.length) publishJobs();
      if (!jobs.length) {
        unsubscribeJobs.get(projectId)?.();
        unsubscribeJobs.delete(projectId);
      }
      monitorActiveProjects();
      // A terminal job leaves the active query. Read only those previously active
      // jobs, avoiding a second permanent query for every project's history.
      for (const previousJob of previousJobs) {
        if (jobs.some((job) => job.id === previousJob.id)) continue;
        try {
          const completed = await getDoc(doc(db, 'projects', projectId, 'sectorResearchJobs', previousJob.id));
          if (completed.exists()) notifyCompletion(projectId, previousJob.id, completed.data());
        } catch (error) {
          if (!disposed && (error as { code?: string }).code !== 'permission-denied') console.error('Error checking background job completion:', error);
        }
      }
    };
    const monitorActiveProjects = () => {
      for (const [projectId, jobs] of jobsByProject) {
        if (unsubscribeJobs.size >= MAX_LIVE_JOB_PROJECTS) break;
        if (!jobs.length || unsubscribeJobs.has(projectId) || !projectIds.has(projectId)) continue;
        const unsubscribe = onSnapshot(activeQuery(projectId), (snapshot) => {
          if (disposed || unsubscribeJobs.get(projectId) !== unsubscribe) return;
          void handleSnapshot(projectId, snapshot);
        }, (error) => {
          if (disposed || unsubscribeJobs.get(projectId) !== unsubscribe) return;
          unsubscribe();
          unsubscribeJobs.delete(projectId);
          jobsByProject.delete(projectId);
          publishJobs();
          if (error.code !== 'permission-denied') console.error(`Error monitoring jobs for project ${projectId}:`, error);
        });
        unsubscribeJobs.set(projectId, unsubscribe);
      }
    };
    const drainDiscovery = async () => {
      if (disposed || draining) return;
      draining = true;
      if (pollTimer) clearTimeout(pollTimer);
      const worker = async () => {
        while (!disposed && pendingProjects.size) {
          const projectId = pendingProjects.values().next().value as string;
          pendingProjects.delete(projectId);
          if (!projectIds.has(projectId) || unsubscribeJobs.has(projectId)) continue;
          try {
            const snapshot = await getDocs(activeQuery(projectId));
            if (!unsubscribeJobs.has(projectId)) await handleSnapshot(projectId, snapshot);
          } catch (error) {
            if (!disposed && (error as { code?: string }).code !== 'permission-denied') console.error(`Error discovering jobs for project ${projectId}:`, error);
          }
        }
      };
      await Promise.all(Array.from({ length: JOB_DISCOVERY_CONCURRENCY }, worker));
      draining = false;
      if (disposed) return;
      if (pendingProjects.size) { void drainDiscovery(); return; }
      pollTimer = setTimeout(() => {
        const includeIdleProjects = Date.now() >= nextIdleSweepAt;
        if (includeIdleProjects) nextIdleSweepAt = Date.now() + IDLE_JOB_POLL_INTERVAL_MS;
        for (const projectId of projectIds) {
          const knownJobs = jobsByProject.get(projectId);
          if (!unsubscribeJobs.has(projectId) && (includeIdleProjects || !knownJobs || knownJobs.length)) pendingProjects.add(projectId);
        }
        void drainDiscovery();
      }, JOB_POLL_INTERVAL_MS);
    };
    refreshProjectRef.current = (projectId) => {
      projectIds.add(projectId);
      const queuedProjects = [...pendingProjects];
      pendingProjects.clear();
      pendingProjects.add(projectId);
      queuedProjects.forEach((queuedProject) => pendingProjects.add(queuedProject));
      void drainDiscovery();
    };
    const unsubscribeProjects = onSnapshot(query(collection(db, 'projects'), where('team', 'array-contains', userId)), (snapshot) => {
      if (disposed) return;
      projectIds = new Set(snapshot.docs.map((document) => document.id));
      for (const [projectId, unsubscribe] of unsubscribeJobs) {
        if (!projectIds.has(projectId)) { unsubscribe(); unsubscribeJobs.delete(projectId); }
      }
      for (const projectId of jobsByProject.keys()) if (!projectIds.has(projectId)) jobsByProject.delete(projectId);
      publishJobs();
      for (const projectId of projectIds) if (!jobsByProject.has(projectId)) pendingProjects.add(projectId);
      void drainDiscovery();
    }, (error) => {
      if (!disposed && error.code !== 'permission-denied') console.error('Error discovering background job projects:', error);
    });
    return () => {
      disposed = true;
      refreshProjectRef.current = () => undefined;
      if (pollTimer) clearTimeout(pollTimer);
      pendingProjects.clear();
      unsubscribeProjects();
      unsubscribeJobs.forEach((unsubscribe) => unsubscribe());
      unsubscribeJobs.clear();
    };
  }, [userId]);

  // ========================================================================
  // OPERATIONS
  // ========================================================================

  const startJob = useCallback(
    (projectId: string, type: BackgroundJob['type'], jobId: string) => {
      if (type === 'sector-research') refreshProjectRef.current(projectId);
      console.log(`Background job started: ${type} (${jobId}) for project ${projectId}`);
    },
    []
  );

  const dismissJob = useCallback((jobId: string) => {
    setActiveJobs((prev) => prev.filter((job) => job.id !== jobId));
  }, []);

  const markNotificationRead = useCallback((notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const getJobStatus = useCallback(
    (jobId: string): BackgroundJob | null => {
      return activeJobs.find((job) => job.id === jobId) || null;
    },
    [activeJobs]
  );

  const unreadCount = notifications.filter((n) => !n.read).length;
  const hasActiveJobs = activeJobs.length > 0;

  return (
    <BackgroundJobsContext.Provider
      value={{
        activeJobs,
        notifications,
        unreadCount,
        startJob,
        dismissJob,
        markNotificationRead,
        markAllRead,
        clearNotifications,
        getJobStatus,
        hasActiveJobs,
      }}
    >
      {children}
    </BackgroundJobsContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useBackgroundJobs() {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error('useBackgroundJobs must be used within BackgroundJobsProvider');
  }
  return context;
}
